// src/components/store/sections/deal/Variant4PastelGrid.tsx
"use client";

import { useState, useRef, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import {
  Star,
  ShoppingBag,
  Clock,
  Flame,
  ArrowRight,
  Heart,
  ChevronLeft,
  ChevronRight,
  Sparkles,
} from "lucide-react";
import { useProductTimer } from "./useProductTimer";

interface Props {
  products: any[];
  subdomain: string;
}

// 🌟 ৫টি ভিন্ন ভিন্ন ৩D শেপ, স্টেজ আর্কিটেকচার এবং ভাইব্র্যান্ট কালার প্যালেট
const CARD_SHAPES_THEMES = [
  {
    // ১. শেপ ১: ক্যাথিড্রাল আর্চ ডোম (স্প্রিং গার্ডেন মিন্ট)
    stageShape: "rounded-t-[80px] rounded-b-[20px]",
    bgGradient: "from-[#a7f3d0] via-[#ecfdf5] to-[#6ee7b7]",
    pedestalGlow: "rgba(16, 185, 129, 0.45)",
    cardBorder: "border-emerald-300/60",
    glowColor: "rgba(16, 185, 129, 0.25)",
    categoryBadge: "bg-emerald-100 text-emerald-800 border-emerald-300",
    priceColor: "text-emerald-700",
    btnGradient: "from-emerald-600 via-teal-600 to-green-600 hover:from-emerald-500 hover:to-teal-500",
  },
  {
    // ২. শেপ ২: ডায়াগনাল অর্গানিক ওয়েভ (ডিপ প্লাম ও ভেলভেট ফিউশা)
    stageShape: "rounded-tl-[80px] rounded-br-[80px] rounded-tr-[24px] rounded-bl-[24px]",
    bgGradient: "from-[#4a044e] via-[#2e0236] to-[#16001d]",
    pedestalGlow: "rgba(217, 70, 239, 0.55)",
    cardBorder: "border-fuchsia-400/50",
    glowColor: "rgba(217, 70, 239, 0.3)",
    categoryBadge: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300",
    priceColor: "text-fuchsia-600",
    btnGradient: "from-purple-600 via-fuchsia-600 to-pink-600 hover:from-purple-500 hover:to-fuchsia-500",
  },
  {
    // ৩. শেপ ৩: সানলিট বেল আর্চ (গোল্ডেন অ্যাম্বার ও বোটানিক্যাল উড)
    stageShape: "rounded-t-[28px] rounded-b-[75px]",
    bgGradient: "from-[#fef08a] via-[#fffbeb] to-[#fde047]",
    pedestalGlow: "rgba(245, 158, 11, 0.5)",
    cardBorder: "border-amber-300/70",
    glowColor: "rgba(245, 158, 11, 0.25)",
    categoryBadge: "bg-amber-100 text-amber-900 border-amber-300",
    priceColor: "text-amber-800",
    btnGradient: "from-amber-600 via-orange-600 to-red-600 hover:from-amber-500 hover:to-orange-500",
  },
  {
    // ৪. শেপ ৪: সাইবার হেক্স-ওয়েভ কাটআউট (ইলেকট্রিক সায়ান ও নিয়ন ব্লু)
    stageShape: "rounded-tr-[80px] rounded-bl-[80px] rounded-tl-[24px] rounded-br-[24px]",
    bgGradient: "from-[#083344] via-[#051d29] to-[#01090f]",
    pedestalGlow: "rgba(6, 182, 212, 0.55)",
    cardBorder: "border-cyan-400/50",
    glowColor: "rgba(6, 182, 212, 0.3)",
    categoryBadge: "bg-cyan-100 text-cyan-800 border-cyan-300",
    priceColor: "text-cyan-600",
    btnGradient: "from-blue-600 via-cyan-600 to-teal-600 hover:from-blue-500 hover:to-cyan-500",
  },
  {
    // ৫. শেপ ৫: সিমেট্রিক্যাল পিল আর্চ (রোজ বেরি সানসেট)
    stageShape: "rounded-t-[90px] rounded-b-[36px]",
    bgGradient: "from-[#fecdd3] via-[#fff1f2] to-[#fda4af]",
    pedestalGlow: "rgba(244, 63, 94, 0.45)",
    cardBorder: "border-rose-300/60",
    glowColor: "rgba(244, 63, 94, 0.25)",
    categoryBadge: "bg-rose-100 text-rose-800 border-rose-300",
    priceColor: "text-rose-600",
    btnGradient: "from-rose-600 via-pink-600 to-purple-600 hover:from-rose-500 hover:to-pink-500",
  },
];

export default function Variant4PastelGrid({ products, subdomain }: Props) {
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [stageWidth, setStageWidth] = useState(1080);

  // Touch swipe coordinates
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        setStageWidth(containerRef.current.clientWidth);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handlePrev = () => {
    setActiveIndex((prev) => (prev - 1 + products.length) % products.length);
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev + 1) % products.length);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    const distance = touchStartX.current - touchEndX.current;
    if (distance > 45) {
      handleNext();
    } else if (distance < -45) {
      handlePrev();
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  if (!products || products.length === 0) return null;

  const isMobile = stageWidth < 640;
  const isTablet = stageWidth >= 640 && stageWidth < 1024;

  const cardWidth = isMobile ? 270 : isTablet ? 320 : 370;
  const spacingX = isMobile ? 120 : isTablet ? 180 : 230;
  const perspectiveVal = isMobile ? 950 : 1300;

  return (
    <section
      ref={containerRef}
      className="max-w-[1440px] mx-auto px-3 sm:px-6 lg:px-8 py-8 sm:py-12 font-sans select-none space-y-7 sm:space-y-10 overflow-hidden"
    >
      {/* 🌟 ১. হেডলাইন সেকশন */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div className="space-y-2">
          {/* Top Badge */}
          <div className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-xs font-black shadow-md shadow-purple-500/20">
            <Sparkles className="w-3.5 h-3.5 text-amber-300" />
            <span>3D Glassy Showcase</span>
          </div>

          {/* Headline */}
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-slate-900 leading-tight">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 via-fuchsia-600 to-pink-600">
              Deals
            </span>{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900">
              of the Day
            </span>{" "}
            <span className="inline-flex items-center px-3 py-0.5 rounded-2xl bg-purple-100 text-purple-700 text-xl sm:text-2xl font-black border border-purple-200">
              ({products.length})
            </span>
          </h2>

          <p className="text-xs sm:text-sm text-slate-600 font-semibold tracking-wide">
            Swipe or click the cards to inspect today&apos;s exclusive limited deals!
          </p>
        </div>

        {/* Arrow Controls */}
        <div className="flex items-center gap-2.5 sm:gap-3">
          <button
            type="button"
            onClick={handlePrev}
            className="w-10 h-10 rounded-full border border-slate-200 bg-white hover:bg-purple-600 hover:text-white text-slate-700 flex items-center justify-center transition-all shadow-sm active:scale-95 cursor-pointer"
            title="Previous Card"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={handleNext}
            className="w-10 h-10 rounded-full border border-slate-200 bg-white hover:bg-purple-600 hover:text-white text-slate-700 flex items-center justify-center transition-all shadow-sm active:scale-95 cursor-pointer"
            title="Next Card"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <Link
            href="/products"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:opacity-95 text-white font-bold text-xs shadow-md transition-all active:scale-95"
          >
            <span>View All Deals</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>

      {/* 🌟 ২. থ্রিডি গ্লাসি কভারফ্লো স্টেজ */}
      <div
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ perspective: `${perspectiveVal}px` }}
        className="relative w-full h-[510px] sm:h-[570px] flex items-center justify-center overflow-visible"
      >
        <div
          style={{ width: `${cardWidth}px` }}
          className="relative h-full flex items-center justify-center"
        >
          {products.map((product: any, idx: number) => {
            const count = products.length;
            let offset = idx - activeIndex;

            if (offset > Math.floor(count / 2)) offset -= count;
            if (offset < -Math.floor(count / 2)) offset += count;

            const isCenter = offset === 0;
            const isLeft = offset < 0;
            const isVisible = Math.abs(offset) <= 2;

            if (!isVisible) return null;

            const translateX = offset * spacingX;
            const translateZ =
              -Math.abs(offset) * (isMobile ? 85 : 120) + (isCenter ? (isMobile ? 20 : 35) : 0);
            const rotateY = isCenter ? 0 : isLeft ? (isMobile ? 26 : 32) : (isMobile ? -26 : -32);
            const scale = isCenter ? 1.03 : Math.max(isMobile ? 0.8 : 0.76, 1 - Math.abs(offset) * 0.14);
            const opacity = isCenter ? 1 : Math.max(isMobile ? 0.42 : 0.48, 1 - Math.abs(offset) * 0.28);
            const zIndex = 30 - Math.abs(offset) * 5;

            const theme = CARD_SHAPES_THEMES[idx % CARD_SHAPES_THEMES.length];

            return (
              <div
                key={product.id || idx}
                onClick={() => setActiveIndex(idx)}
                style={{
                  transform: `translateX(${translateX}px) translateZ(${translateZ}px) rotateY(${rotateY}deg) scale(${scale})`,
                  opacity,
                  zIndex,
                  transformStyle: "preserve-3d",
                }}
                className="absolute inset-0 transition-all duration-500 ease-out cursor-pointer select-none"
              >
                <GlassyShapeCard
                  product={product}
                  theme={theme}
                  isCenter={isCenter}
                  isMobile={isMobile}
                  subdomain={subdomain}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* 🌟 ৩. ডটস ইন্ডিকেটর */}
      <div className="flex items-center justify-center gap-2 pt-1">
        {products.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setActiveIndex(i)}
            className={`h-2 rounded-full transition-all cursor-pointer ${
              activeIndex === i
                ? "w-8 bg-gradient-to-r from-purple-600 to-indigo-600 shadow-sm"
                : "w-2 bg-slate-300 hover:bg-slate-400"
            }`}
            title={`Slide ${i + 1}`}
          />
        ))}
      </div>
    </section>
  );
}

// 🌟 ৩D শেপসহ প্রিমিয়াম গ্লাসি কার্ড
function GlassyShapeCard({
  product,
  theme,
  isCenter,
  isMobile,
  subdomain,
}: {
  product: any;
  theme: (typeof CARD_SHAPES_THEMES)[0];
  isCenter: boolean;
  isMobile: boolean;
  subdomain: string;
}) {
  const timer = useProductTimer(product);
  const [isWishlisted, setIsWishlisted] = useState(false);

  const rawImage = product?.primary_image || product?.image;

  // ১. Normal fallback image URL (Variant 1, 2, 3 er moto)
  const getNormalUrl = (raw?: string | null) => {
    if (!raw) return null;
    if (raw.startsWith("http://") || raw.startsWith("https://") || raw.startsWith("data:")) {
      if (raw.includes("127.0.0.1:8000")) {
        return raw.replace("127.0.0.1:8000", `${subdomain}.localhost:8000`);
      }
      return raw;
    }
    const clean = raw.startsWith("/") ? raw : `/${raw}`;
    return `http://${subdomain}.localhost:8000${clean}`;
  };

  // ২. AI Background Remover URL (Apnar original code)
  const getCutoutUrl = (raw?: string | null) => {
    if (!raw) return null;
    let fullSource = raw;
    if (!raw.startsWith("http://") && !raw.startsWith("https://") && !raw.startsWith("data:")) {
      const clean = raw.startsWith("/") ? raw : `/${raw}`;
      fullSource = `http://${subdomain}.localhost:8000${clean}`;
    } else if (raw.includes("127.0.0.1:8000")) {
      fullSource = raw.replace("127.0.0.1:8000", `${subdomain}.localhost:8000`);
    }

    return `http://${subdomain}.localhost:8000/api/v1/ai/motion/deal-cutout/?url=${encodeURIComponent(
      fullSource
    )}`;
  };

  const normalUrl = getNormalUrl(rawImage);
  const cutoutUrl = getCutoutUrl(rawImage);

  // Local-e prothome AI Cutout try korbe
  const [imgSrc, setImgSrc] = useState<string | null>(cutoutUrl || normalUrl);

  useEffect(() => {
    setImgSrc(cutoutUrl || normalUrl);
  }, [rawImage, subdomain]);

  const currentPrice = Number(product?.price || 0);
  const oldPrice = product?.compare_price ? Number(product.compare_price) : null;
  const discountPercent =
    oldPrice && oldPrice > currentPrice
      ? Math.round(((oldPrice - currentPrice) / oldPrice) * 100)
      : 33;

  const slug = product?.slug || product?.id || "";

  return (
    <div
      style={{
        boxShadow: isCenter ? `0 24px 50px -10px ${theme.glowColor}` : "0 8px 24px rgba(0,0,0,0.06)",
      }}
      className={`w-full h-full rounded-[32px] p-4 sm:p-5 bg-white/95 backdrop-blur-2xl border ${
        isCenter ? theme.cardBorder : "border-slate-200/80"
      } flex flex-col justify-between relative overflow-hidden group transition-all duration-300`}
    >
      <div>
        {/* ১. টপ বার: ডিসকাউন্ট ব্যাজ ও উইশলিস্ট */}
        <div className="flex items-center justify-between gap-2 mb-2 sm:mb-3 z-10 relative">
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-rose-500 text-white font-black text-[10px] shadow-sm tracking-wide">
            <Flame className="w-3.5 h-3.5 fill-white" />
            <span>{discountPercent}% OFF</span>
          </span>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsWishlisted(!isWishlisted);
            }}
            className="w-8 h-8 rounded-full bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-400 hover:text-rose-500 hover:bg-rose-50 transition-colors shadow-2xs cursor-pointer active:scale-90"
            title="Wishlist"
          >
            <Heart
              className={`w-4 h-4 transition-colors ${
                isWishlisted ? "fill-rose-500 text-rose-500" : ""
              }`}
            />
          </button>
        </div>

        {/* 🌟 ২. ইউনিক ৩D আর্কিটেকচারাল ইমেজ শেপ (কাটআউট ও অটো-ফলব্যাক) */}
        <div
          className={`relative w-full ${
            isMobile ? "h-44" : "h-52"
          } ${theme.stageShape} bg-gradient-to-b ${theme.bgGradient} overflow-hidden flex items-center justify-center p-3 mb-3.5 shadow-inner border border-white/40`}
        >
          {/* ৩D পেডেস্টাল আভা */}
          <div
            style={{ backgroundColor: theme.pedestalGlow }}
            className="absolute bottom-2.5 w-36 h-7 rounded-full blur-md"
          />

          <div className="relative w-full h-full flex items-center justify-center z-10">
            {imgSrc ? (
              <Image
                src={imgSrc}
                alt={product?.name || "Deal Item"}
                fill
                unoptimized
                onError={() => {
                  // Bairer network ba onno device-e localhost fail korle shathe shathe normal image load hobe
                  if (normalUrl && imgSrc !== normalUrl) {
                    setImgSrc(normalUrl);
                  }
                }}
                className="object-contain p-2 group-hover:scale-110 transition-transform duration-500 drop-shadow-[0_16px_22px_rgba(0,0,0,0.35)]"
              />
            ) : (
              <div className="text-slate-400 text-xs font-bold font-mono">No Image</div>
            )}
          </div>

          {/* ওভারল্যাপিং ক্যাটাগরি ব্যাজ */}
          <span
            className={`absolute bottom-2.5 left-2.5 px-3 py-0.5 rounded-full text-[9px] font-bold border shadow-xs truncate max-w-[150px] ${theme.categoryBadge}`}
          >
            {product?.category_name || "Special Deal"}
          </span>
        </div>

        {/* ৩. টাইটেল, রেটিং ও প্রাইস */}
        <div className="space-y-1">
          <Link
            href={`/products/${slug}`}
            className="text-base font-black text-slate-900 hover:text-purple-600 transition-colors line-clamp-1 block leading-snug"
          >
            {product?.name || "Deal Item"}
          </Link>

          <div className="flex items-center gap-1 text-xs font-bold text-amber-500">
            <Star className="w-3.5 h-3.5 fill-amber-400" />
            <span>5.0 (124 reviews)</span>
          </div>

          <div className="flex items-baseline gap-2 mt-1.5 font-mono">
            <span className={`text-2xl font-black ${theme.priceColor}`}>
              ৳{currentPrice.toLocaleString()}
            </span>
            {oldPrice && (
              <span className="text-xs text-slate-400 line-through">
                ৳{oldPrice.toLocaleString()}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ৪. লাইভ টাইমার এবং "Shop Now" বাটন */}
      <div className="space-y-3 mt-3 pt-3 border-t border-slate-100">
        <div className="bg-slate-50 rounded-2xl p-2.5 border border-slate-100 space-y-1">
          <div className="flex items-center gap-1 text-[10px] font-bold text-rose-500">
            <Clock className="w-3.5 h-3.5" />
            <span>Hurry Up! Offer ends in:</span>
          </div>

          <div className="flex items-center justify-between text-center font-mono">
            <div className="flex-1 bg-white py-1 px-1 rounded-xl border border-slate-200 shadow-2xs">
              <span className="text-xs font-black text-slate-800 block leading-tight">
                {String(timer.hours).padStart(2, "0")}h
              </span>
              <span className="text-[7px] font-bold text-slate-400 uppercase tracking-tight block">
                Hours
              </span>
            </div>
            <span className="px-1 text-slate-400 font-bold">:</span>
            <div className="flex-1 bg-white py-1 px-1 rounded-xl border border-slate-200 shadow-2xs">
              <span className="text-xs font-black text-slate-800 block leading-tight">
                {String(timer.minutes).padStart(2, "0")}m
              </span>
              <span className="text-[7px] font-bold text-slate-400 uppercase tracking-tight block">
                Minutes
              </span>
            </div>
            <span className="px-1 text-slate-400 font-bold">:</span>
            <div className="flex-1 bg-white py-1 px-1 rounded-xl border border-slate-200 shadow-2xs">
              <span className="text-xs font-black text-rose-600 block leading-tight">
                {String(timer.seconds).padStart(2, "0")}s
              </span>
              <span className="text-[7px] font-bold text-slate-400 uppercase tracking-tight block">
                Seconds
              </span>
            </div>
          </div>
        </div>

        {/* বাটন: Shop Now */}
        <Link
          href={`/products/${slug}`}
          className={`w-full py-2.5 px-4 rounded-full bg-gradient-to-r ${theme.btnGradient} text-white font-black text-xs shadow-md flex items-center justify-center gap-2 active:scale-95 transition-all cursor-pointer`}
        >
          <ShoppingBag className="w-4 h-4" />
          <span>Shop Now</span>
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}